import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle, Check, ArrowLeft, Loader2 } from 'lucide-react';
import '../styles/auth.css';
import { API_BASE_URL } from '../../src/config';

const PASSWORD_REQUIREMENTS = [
  { label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { label: 'Uppercase letter (A-Z)', test: (v) => /[A-Z]/.test(v) },
  { label: 'Lowercase letter (a-z)', test: (v) => /[a-z]/.test(v) },
  { label: 'Number (0-9)', test: (v) => /[0-9]/.test(v) },
  { label: 'Special character (!@#$%)', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const reqResults = useMemo(
    () => PASSWORD_REQUIREMENTS.map((r) => ({ ...r, met: r.test(password) })),
    [password],
  );
  const allMet = reqResults.every((r) => r.met);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allMet) {
      setStatus({ type: 'error', message: 'Please meet all password requirements.' });
      return;
    }
    if (password !== confirmPassword) {
      setStatus({ type: 'error', message: 'Passwords do not match.' });
      return;
    }
    if (!token || !email) {
      setStatus({ type: 'error', message: 'Invalid reset link. Please request a new one.' });
      return;
    }

    setLoading(true);
    setStatus(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Reset failed.');
      setStatus({ type: 'success', message: data.message });
    } catch (err) {
      setStatus({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const isSuccess = status?.type === 'success';

  return (
    <div className="auth-page">
      <div className="auth-page__body">
        <div className="auth-regis-page__inner">
          <div className="auth-card" style={{ maxWidth: 440 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: 56, height: 56, borderRadius: 16,
                background: isSuccess
                  ? 'linear-gradient(135deg, #22c55e, #16a34a)'
                  : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                color: '#fff', display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', marginBottom: 16
              }}>
                {isSuccess ? <CheckCircle size={28} /> : <Lock size={28} />}
              </div>
              <h2 style={{ margin: '0 0 8px', fontSize: 24, fontWeight: 700, color: '#0f172a' }}>
                {isSuccess ? 'Password Reset' : 'Create New Password'}
              </h2>
              {!isSuccess && (
                <p style={{ margin: 0, color: '#64748b', fontSize: 15, lineHeight: 1.6 }}>
                  Enter your new password below.
                </p>
              )}
            </div>

            {status && (
              <div
                className="auth-card__field-error"
                style={status.type === 'success' ? {
                  background: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.25)',
                  color: '#15803d',
                } : {}}
              >
                {status.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                <span>{status.message}</span>
              </div>
            )}

            {isSuccess ? (
              <div className="auth-card__footer">
                <Link to="/login" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '14px 24px', borderRadius: 14,
                  background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                  color: '#fff', fontWeight: 600, textDecoration: 'none', fontSize: 15,
                  justifyContent: 'center', width: '100%'
                }}>
                  Go to Login
                </Link>
              </div>
            ) : (
              <form className="auth-card__form" onSubmit={handleSubmit}>
                <div className="auth-card__field">
                  <label htmlFor="new-password">New Password</label>
                  <div className="auth-card__input">
                    <Lock size={18} />
                    <input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoFocus
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0 }}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {reqResults.map((r, i) => (
                    <div key={i} className="req-item" style={{
                      display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
                      color: r.met ? '#16a34a' : '#94a3b8',
                      transition: 'color 0.2s'
                    }}>
                      <Check size={14} style={{ opacity: r.met ? 1 : 0.4 }} />
                      {r.label}
                    </div>
                  ))}
                </div>

                <div className="auth-card__field">
                  <label htmlFor="confirm-password">Confirm Password</label>
                  <div className={`auth-card__input ${confirmPassword && password !== confirmPassword ? 'auth-card__input--error' : ''}`}>
                    <Lock size={18} />
                    <input
                      id="confirm-password"
                      type={showConfirm ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0 }}>
                      {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <button type="submit" className="auth-card__submit" disabled={loading || !allMet}>
                  {loading ? <Loader2 size={18} className="spin" /> : null}
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            )}

            {!isSuccess && (
              <div className="auth-card__footer">
                <Link to="/forgot-password" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  color: '#2563eb', fontWeight: 600, textDecoration: 'none', fontSize: 14
                }}>
                  <ArrowLeft size={16} /> Request new link
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
